import React, { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { ThemeProvider } from '@mui/material'
import { getTheme } from 'themes'

import Alert from 'components_new/atoms/Alert'
import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Collapse from 'components_new/atoms/Collapse'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import List from 'components_new/atoms/List'
import ListItem from 'components_new/atoms/List/ListItem'
import ListItemText from 'components_new/atoms/List/ListItemText'
import TextField from 'components_new/atoms/TextField'

import AdvancedDialog from 'components_new/molecules/AdvancedDialog'
import CollapsibleSection from 'components_new/molecules/CollapsibleSection'
import CustomListItem from 'components_new/molecules/GenericListItem'
import SelectSearch from 'components_new/molecules/SelectSearch'
import SimpleDialog from 'components_new/molecules/SimpleDialog'
import UploadFile from 'components_new/molecules/UploadFile'

import type { TCompanyDto, TCompanyGroupDto } from '@homepal/api-contract'
import { EconomyAccount, ParsedEconomySet } from 'types/GlobalEconomySets'
import ListItemAvatar from 'components_new/atoms/List/ListItemAvatar'
import ListItemSecondaryAction from 'components_new/atoms/List/ListItemSecondaryAction'
import { useTranslation } from 'language/translator'
import { useIsManagementCompany } from 'redux/hooks/Customer'
import { sortAlphabeticalAsc } from 'utils/sortHelper'

enum Tab {
  DEFAULT = 'DEFAULT',
  CHART = 'CHART'
}

interface EditChartOfAccountsDialogProps {
  availableCompanies: TCompanyDto[]
  economySet: ParsedEconomySet
  companyGroupsWithoutEconomySets: TCompanyGroupDto[]
  createEconomyAccount: (
    data: {
      name: string
      number: string
      economy_set_id: string
    },
    callback?: (err?: boolean) => void
  ) => void
  deleteEconomyAccount: (
    id: string,
    economySetId: string,
    callback?: (err?: boolean) => void
  ) => void
  integrateCompanyGroups: boolean
  onClose: () => void
  open: boolean
  // Opens on the accounts, e.g. right after creating an empty chart.
  openAccounts?: boolean
  updateCompanies: (
    id: string,
    companyIds: string[],
    callback?: (err?: boolean) => void
  ) => void
  updateCompanyGroup: (
    id: string,
    name?: string,
    economySetId?: string | null,
    callback?: (err?: boolean) => void
  ) => void
  updateEconomyAccount: (
    id: string,
    name: string,
    economySetId: string,
    callback?: (err?: boolean) => void
  ) => void
  updateEconomySet: (
    id: string,
    name: string,
    callback?: (err?: boolean) => void
  ) => void
  uploadEconomySet: (
    id: string,
    csvFile: Blob,
    callback?: (err?: boolean) => void
  ) => void
}

const EditChartOfAccountsDialog = (props: EditChartOfAccountsDialogProps) => {
  const {
    createEconomyAccount,
    deleteEconomyAccount,
    economySet,
    integrateCompanyGroups,
    updateCompanies,
    updateCompanyGroup,
    updateEconomySet,
    updateEconomyAccount,
    uploadEconomySet
  } = props

  const { t } = useTranslation()

  const isManagementCompany = useIsManagementCompany()

  // A regular company's extra chart of accounts applies to chosen companies,
  // which get a tab of their own next to the accounts like company groups.
  const isCompanySpecific = !isManagementCompany && !economySet.is_default
  const hasAssignmentTab = isManagementCompany || isCompanySpecific

  // Loaders
  const [updatingSet, setUpdatingSet] = useState<boolean>(false)
  const [updatingCompanyGroup, setUpdatingCompanyGroup] =
    useState<boolean>(false)
  const [updatingCompanies, setUpdatingCompanies] = useState<boolean>(false)

  const [addCompanyGroupOpen, setAddCompanyGroupOpen] = useState(false)
  const [createAccountOpen, setCreateAccountOpen] = useState(false)
  const [renameOpen, setRenameOpen] = useState(false)
  const [deleteAccount, setDeleteAccount] = useState<EconomyAccount | null>(
    null
  )
  const [editAccount, setEditAccount] = useState<EconomyAccount | null>(null)
  const [editAccountNameInput, setEditAccountNameInput] = useState('')
  const [createAccountNameInput, setCreateAccountNameInput] = useState('')
  const [createAccountNumberInput, setCreateAccountNumberInput] = useState('')
  const [errorAccountNumber, setErrorAccountNumber] = useState<string | null>(
    null
  )
  const [newName, setNewName] = useState<string>(economySet.name)
  const [tab, setTab] = useState<Tab>(
    props.openAccounts ? Tab.CHART : Tab.DEFAULT
  )
  const [newEconomySetFile, setEconomySetFile] = useState<{
    file: Blob
    name: string
  } | null>(null)
  const [uploading, setUploading] = useState<boolean>(false)

  useEffect(() => {
    if (economySet.name !== newName) {
      setNewName(economySet.name)
    }
  }, [economySet])
  const parsedAccounts = useMemo(
    () => economySet.accounts.map((account) => account),
    [economySet]
  )

  const allAccounts = useMemo(() => {
    const list = []

    for (let i = 1000; i <= 9999; i++) {
      list.push(i.toString())
    }

    return list
  }, [])

  const handleCloseRename = () => {
    setRenameOpen(false)

    setNewName(economySet.name)
  }

  const handleCloseEditAccount = () => {
    setEditAccount(null)
  }

  const handleCloseCreateAccount = () => {
    setCreateAccountOpen(false)

    setCreateAccountNameInput('')
    setCreateAccountNumberInput('')
    setErrorAccountNumber(null)
  }

  const onChangeAccountNumber = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setCreateAccountNumberInput(e.target.value)

    if (parsedAccounts.some((acc) => acc.number === e.target.value)) {
      setErrorAccountNumber(
        t('chart_of_accounts', 'account_number_exists_error')
      )
    } else if (!allAccounts.includes(e.target.value)) {
      setErrorAccountNumber(
        t('chart_of_accounts', 'account_number_invalid_error')
      )
    } else {
      setErrorAccountNumber(null)
    }
  }

  useEffect(() => {
    if (props.open) {
      setNewName(economySet.name)
    } else {
      setTab(Tab.DEFAULT)
    }
  }, [props.open])

  return (
    <ThemeProvider theme={getTheme('light')}>
      <AdvancedDialog
        actions={
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between'
            }}
          >
            {tab === Tab.DEFAULT && hasAssignmentTab ? (
              <Button
                disabled={updatingCompanyGroup}
                startIcon={<Icon name="AccountTreeOutlined" />}
                variant="outlined"
                onClick={() => setTab(Tab.CHART)}
              >
                {t('chart_of_accounts', 'edit_accounts')}
              </Button>
            ) : (
              <Box />
            )}
            <Button variant="text" onClick={props.onClose}>
              {t('global', 'close')}
            </Button>
          </Box>
        }
        onClose={props.onClose}
        open={props.open}
        title={
          tab === Tab.DEFAULT ? (
            t('chart_of_accounts', 'edit_chart_of_accounts')
          ) : (
            <Box
              component="span"
              onClick={() => setTab(Tab.DEFAULT)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                color: 'text.secondary',
                '&:hover': {
                  color: 'text.primary'
                },
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis'
              }}
            >
              <Icon name="ArrowBackOutlined" sx={{ mr: 0.5 }} />
              {t('chart_of_accounts', 'edit_accounts')}
            </Box>
          )
        }
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth:
              tab === Tab.CHART || !hasAssignmentTab
                ? 'md'
                : '512px!important',
            transition: 'max-width 0.5s ease'
          }
        }}
      >
        <Collapse in={tab === Tab.DEFAULT}>
          <CustomListItem
            component="div"
            title={economySet?.name || ''}
            sx={{ bgcolor: 'grey.200', mb: 2 }}
            actions={
              <IconButton
                disabled={updatingCompanyGroup}
                disableRipple={updatingCompanyGroup}
                onClick={() => setRenameOpen(true)}
              >
                <Icon color={'disabled'} fontSize="small" name={'Edit'} />
              </IconButton>
            }
          />
        </Collapse>
        <Collapse in={tab === Tab.DEFAULT && isManagementCompany}>
          <CollapsibleSection
            actions={
              integrateCompanyGroups ? null : (
                <Button
                  disabled={updatingCompanyGroup}
                  onClick={() => setAddCompanyGroupOpen(true)}
                  startIcon={<Icon name="AddCircleOutlineOutlined" />}
                >
                  {t('global', 'add')}
                </Button>
              )
            }
            size="medium"
            title={t('global', 'company_groups')}
            variant="blank"
            sx={{ p: 0 }}
          >
            <List>
              {[...economySet.companyGroups]
                .sort((a, b) => sortAlphabeticalAsc(a.name, b.name))
                .map((group) => (
                  <ListItem
                    key={group.id}
                    secondaryAction={
                      integrateCompanyGroups ? null : (
                        <Button
                          disabled={updatingCompanyGroup}
                          onClick={() => {
                            setUpdatingCompanyGroup(true)
                            updateCompanyGroup(group.id, undefined, null, () =>
                              setUpdatingCompanyGroup(false)
                            )
                          }}
                          variant="text"
                        >
                          {t('global', 'delete')}
                        </Button>
                      )
                    }
                  >
                    <Icon color="disabled" name="Business" />
                    <ListItemText
                      sx={{ ml: 3 }}
                      primary={group.name}
                      primaryTypographyProps={{ fontWeight: 500 }}
                    />
                  </ListItem>
                ))}
            </List>
          </CollapsibleSection>
        </Collapse>
        <Collapse in={tab === Tab.DEFAULT && isCompanySpecific}>
          <CollapsibleSection
            size="medium"
            title={t('global', 'companies')}
            variant="blank"
            sx={{ p: 0 }}
          >
            <Alert severity="info" icon={<Icon name="Info" />} sx={{ mb: 2 }}>
              {t('chart_of_accounts', 'companies_helper_text')}
            </Alert>
            <SelectSearch
              closeOnSelect={false}
              disabled={updatingCompanies}
              helperText={t('chart_of_accounts', 'companies_required')}
              label={t('global', 'companies')}
              multiple={true}
              onChange={(options) => {
                // A company-specific chart of accounts keeps at least one.
                if (options.length === 0) {
                  return
                }

                // Deactivated companies are hidden from the picker, so keep
                // them to not drop their chart of accounts on every save.
                const shownIds = new Set(
                  economySet.companies.map((company) => company.id)
                )
                const hiddenIds = economySet.company_ids.filter(
                  (id) => !shownIds.has(id)
                )

                const selectedIds = options.map((option) => option.id as string)

                setUpdatingCompanies(true)
                updateCompanies(
                  economySet.id,
                  [...selectedIds, ...hiddenIds],
                  () => setUpdatingCompanies(false)
                )
              }}
              options={props.availableCompanies.map((company) => ({
                id: company.id,
                title: company.name
              }))}
              selected={economySet.companies.map((company) => ({
                id: company.id,
                title: company.name
              }))}
            />
          </CollapsibleSection>
        </Collapse>
        {tab === Tab.CHART || !hasAssignmentTab ? (
          <>
            <Collapse in={tab === Tab.CHART || !hasAssignmentTab}>
              <CollapsibleSection
                actions={
                  <Button
                    onClick={() => setCreateAccountOpen(true)}
                    startIcon={<Icon name="AddCircleOutlineOutlined" />}
                  >
                    {t('global', 'add')}
                  </Button>
                }
                size="medium"
                title={t('accounting', 'accounts')}
                variant="blank"
                sx={{ p: 0 }}
              >
                <List>
                  {[...parsedAccounts]
                    .sort((a, b) => sortAlphabeticalAsc(a.number, b.number))
                    .map((account) => (
                      <ListItem
                        key={account.number}
                        secondaryAction={
                          <>
                            <Button
                              onClick={() => {
                                setEditAccountNameInput(account.name || '')

                                setEditAccount(account)
                              }}
                              variant="text"
                            >
                              {t('global', 'edit')}
                            </Button>
                            <Button
                              onClick={() => setDeleteAccount(account)}
                              variant="text"
                            >
                              {t('global', 'delete')}
                            </Button>
                          </>
                        }
                      >
                        <ListItemText
                          primary={`${account.number} ${account.name}`}
                          primaryTypographyProps={{ fontWeight: 500 }}
                        />
                      </ListItem>
                    ))}
                </List>
              </CollapsibleSection>
            </Collapse>
            <Collapse
              in={tab === Tab.CHART || !hasAssignmentTab}
              sx={{ mb: 2 }}
            >
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1
                }}
              >
                <Alert severity="info" icon={<Icon name="Info" />}>
                  {t('chart_of_accounts', 'upload_instructions')}
                </Alert>
                <UploadFile
                  accept={['text/csv']}
                  maxSize={2}
                  uploadFile={(file) =>
                    setEconomySetFile({ file, name: file.name })
                  }
                />
                {newEconomySetFile ? (
                  <ListItem
                    component="div"
                    sx={{ bgcolor: 'action.hover', mt: 1 }}
                  >
                    <ListItemAvatar>
                      <Box
                        sx={{
                          position: 'relative',
                          height: '48px',
                          width: '48px',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Icon name={'AttachmentOutlined'} />
                      </Box>
                    </ListItemAvatar>
                    <ListItemText primary={newEconomySetFile.name} />
                    <ListItemSecondaryAction>
                      <IconButton onClick={() => setEconomySetFile(null)}>
                        <Icon name="Delete" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ) : null}
                <Button
                  fullWidth
                  disabled={!newEconomySetFile}
                  loading={uploading}
                  variant="contained"
                  size="large"
                  sx={{ mt: 2 }}
                  onClick={() => {
                    setUploading(true)
                    uploadEconomySet(
                      (economySet as ParsedEconomySet).id,
                      newEconomySetFile?.file as Blob,
                      (err) => {
                        if (!err) {
                          setEconomySetFile(null)
                        }

                        setUploading(false)
                      }
                    )
                  }}
                >
                  {t('global', 'upload')}
                </Button>
              </Box>
            </Collapse>
          </>
        ) : null}
      </AdvancedDialog>

      {/* Edit chart of accounts */}
      <SimpleDialog
        actions={
          <Button variant="text" onClick={handleCloseRename}>
            {t('global', 'close')}
          </Button>
        }
        onClose={handleCloseRename}
        open={renameOpen}
        title={t('chart_of_accounts', 'edit_account')}
      >
        <TextField
          fullWidth={true}
          label={t('global', 'chart_of_accounts')}
          autoComplete="off"
          value={newName}
          onChange={(event) => setNewName(event.target.value)}
          sx={{ mt: 1, mb: 2 }}
          required
        />
        <Button
          disabled={newName.length === 0}
          fullWidth
          loading={updatingSet}
          variant="contained"
          size="large"
          onClick={() => {
            setUpdatingSet(true)
            updateEconomySet(
              (economySet as ParsedEconomySet).id,
              newName,
              (err) => {
                if (!err) {
                  handleCloseRename()
                }

                setUpdatingSet(false)
              }
            )
          }}
        >
          {t('global', 'save')}
        </Button>
      </SimpleDialog>

      {/* Create account modal */}
      <SimpleDialog
        actions={
          <Button variant="text" onClick={handleCloseCreateAccount}>
            {t('global', 'close')}
          </Button>
        }
        onClose={handleCloseCreateAccount}
        open={createAccountOpen}
        title={t('chart_of_accounts', 'add_account')}
      >
        <TextField
          fullWidth={true}
          label={t('chart_of_accounts', 'account_number')}
          autoComplete="off"
          value={createAccountNumberInput}
          onChange={onChangeAccountNumber}
          sx={{ mt: 1, mb: 2 }}
          required
          type="number"
          helperText={errorAccountNumber}
          error={Boolean(errorAccountNumber)}
        />
        <TextField
          fullWidth={true}
          label={t('global', 'name')}
          autoComplete="off"
          value={createAccountNameInput}
          onChange={(event) => setCreateAccountNameInput(event.target.value)}
          sx={{ mt: 1, mb: 2 }}
          required
        />
        <Button
          disabled={
            createAccountNameInput.length === 0 ||
            createAccountNumberInput.length === 0 ||
            Boolean(errorAccountNumber)
          }
          fullWidth
          loading={updatingSet}
          variant="contained"
          size="large"
          onClick={() => {
            setUpdatingSet(true)
            createEconomyAccount(
              {
                name: createAccountNameInput,
                number: createAccountNumberInput,
                economy_set_id: (economySet as ParsedEconomySet).id
              },
              (err) => {
                if (!err) {
                  handleCloseCreateAccount()
                }

                setUpdatingSet(false)
              }
            )
          }}
        >
          {t('global', 'save')}
        </Button>
      </SimpleDialog>

      {/* Edit account modal */}
      <SimpleDialog
        actions={
          <Button variant="text" onClick={handleCloseEditAccount}>
            {t('global', 'close')}
          </Button>
        }
        onClose={handleCloseEditAccount}
        open={Boolean(editAccount)}
        title={t('chart_of_accounts', 'edit_account')}
      >
        <TextField
          fullWidth={true}
          label={t('global', 'name')}
          autoComplete="off"
          value={editAccountNameInput}
          onChange={(event) => setEditAccountNameInput(event.target.value)}
          sx={{ mt: 1, mb: 2 }}
          required
        />
        <Button
          disabled={editAccountNameInput.length === 0}
          fullWidth
          loading={updatingSet}
          variant="contained"
          size="large"
          onClick={() => {
            setUpdatingSet(true)
            updateEconomyAccount(
              (editAccount as EconomyAccount).id,
              editAccountNameInput,
              (economySet as ParsedEconomySet).id,
              (err) => {
                if (!err) {
                  handleCloseEditAccount()
                }

                setUpdatingSet(false)
              }
            )
          }}
        >
          {t('global', 'save')}
        </Button>
      </SimpleDialog>

      {/* Add company group to chart of accounts modal */}
      <SimpleDialog
        actions={
          <Button variant="text" onClick={() => setAddCompanyGroupOpen(false)}>
            {t('global', 'close')}
          </Button>
        }
        onClose={() => setAddCompanyGroupOpen(false)}
        open={addCompanyGroupOpen}
        title={t('company_groups', 'add_company_group')}
      >
        {props.companyGroupsWithoutEconomySets.length > 0 ? (
          <List sx={{ position: 'relative' }}>
            {[...props.companyGroupsWithoutEconomySets]
              .sort((a, b) => sortAlphabeticalAsc(a.name, b.name))
              .map((group) => (
                <ListItem
                  key={group.id}
                  secondaryAction={
                    <Button
                      disabled={
                        updatingCompanyGroup || group.companies.length === 0
                      }
                      onClick={() => {
                        setUpdatingCompanyGroup(true)
                        updateCompanyGroup(
                          group.id,
                          undefined,
                          (economySet as ParsedEconomySet).id,
                          () => {
                            setUpdatingCompanyGroup(false)
                          }
                        )
                      }}
                      variant="text"
                    >
                      {t('global', 'add')}
                    </Button>
                  }
                >
                  <Icon color="disabled" name="Business" />
                  <ListItemText
                    sx={{ ml: 3 }}
                    primary={`${group.name} ${
                      group.companies.length === 0
                        ? t('company_groups', 'missing_companies')
                        : ''
                    }`}
                    primaryTypographyProps={{
                      fontWeight: 500,
                      color:
                        group.companies.length === 0
                          ? 'text.disabled'
                          : undefined
                    }}
                  />
                </ListItem>
              ))}
          </List>
        ) : (
          <Alert severity="warning" icon={<Icon name="Warning" />}>
            {t('company_groups', 'no_company_groups_without_chart_of_accounts')}
          </Alert>
        )}
      </SimpleDialog>

      {/* Delete account from chart modal */}
      <SimpleDialog
        title={t('chart_of_accounts', 'delete_account')}
        open={Boolean(deleteAccount)}
        onClose={() => setDeleteAccount(null)}
        actions={
          <>
            <Button variant="text" onClick={() => setDeleteAccount(null)}>
              {t('global', 'close')}
            </Button>
            <Button
              color="error"
              loading={updatingSet}
              onClick={() => {
                setUpdatingSet(true)

                deleteEconomyAccount(
                  (deleteAccount as EconomyAccount).id,
                  (economySet as ParsedEconomySet).id,
                  (err) => {
                    if (!err) {
                      setDeleteAccount(null)
                    }

                    setUpdatingSet(false)
                  }
                )
              }}
              variant="contained"
            >
              {t('global', 'delete')}
            </Button>
          </>
        }
        contentText={
          <>
            <code>
              {deleteAccount?.number} {deleteAccount?.name}
            </code>{' '}
            {t('guides', 'delete_confirmation_suffix')}
          </>
        }
      />
    </ThemeProvider>
  )
}

export default EditChartOfAccountsDialog
